import React from 'react';
import { arPdf } from '../../../utils/filtyper';

/**
 * Miniatyren av prislistans exempelbilaga.
 *
 * En bild beskärs med object-cover och kan därför ligga i vilken ruta som
 * helst. En PDF kan inte beskäras — visaren skalar sidan, och det som inte får
 * plats hamnar utanför. Ligger en A4-sida i en kvadrat syns bara en remsa av
 * den, vilket såg avhugget ut.
 *
 * Lösningen är att ge rutan sidans form. Prisexemplen är **liggande** A4,
 * 297×210 mm, alltid — aldrig stående (bekräftat av Emelie 2026-10-12).
 *
 * `view=Fit` skalar in hela sidan i rutan, till skillnad från `FitH` som bara
 * anpassar bredden. För en liggande sida i en liggande ruta är de likvärdiga,
 * men Fit kapar inget om någon mot förmodan lägger in en stående bilaga.
 *
 * Geometrin bor här och inte i vyerna, eftersom fyra ställen visar samma
 * miniatyr: IA:s panel, tabellvyn, presentationens läsvy och kundens modal.
 */
const A4_LIGGANDE = 'aspect-[297/210]';

// toolbar/navpanes/scrollbar bort så miniatyren visar sidan och inget annat.
// page=1 är explicit: utan den minns vissa visare var man var senast.
const VISNINGSPARAMETRAR = '#toolbar=0&navpanes=0&scrollbar=0&view=Fit&page=1';

/**
 * Klasserna för rutan miniatyren ligger i.
 *
 * @param {string} src
 * @param {Object} opts
 * @param {string} opts.bredd      - Breddklass för BILDER, t.ex. 'w-20'
 * @param {string} [opts.breddPdf] - Breddklass för PDF. En liggande ruta är
 *   låg, så samma bredd som en bild ger en mycket liten yta — därför får PDF:en
 *   vara bredare. Utan värde används `bredd`.
 * @param {string} [opts.bildHojd] - Höjdklass för BILDER. Tom sträng låter
 *   bilden fylla radens höjd via `items-stretch`, vilket är vad läsvyn och
 *   kundmodalen gör. PDF:en struntar i den och använder A4-formen.
 * @returns {string}
 */
export const bilageRutaKlass = (src, { bredd, breddPdf, bildHojd = '' }) =>
  arPdf(src)
    // self-start: utan den sträcker `items-stretch` ut rutan till radens höjd
    // och aspektförhållandet får ingen verkan.
    ? `${breddPdf || bredd} ${A4_LIGGANDE} self-start`
    : `${bredd} ${bildHojd}`.trim();

/** Innehållet i rutan — en bild eller första sidan av en PDF. */
export const BilageInnehall = ({ src, alt = '' }) => {
  if (!src) return null;
  if (!arPdf(src)) {
    return <img src={src} alt={alt} className="w-full h-full object-cover" />;
  }
  return (
    // pointer-events-none: en <embed> äter annars klicket, så förstoringen
    // aldrig öppnas. Vyerna lägger en genomskinlig yta ovanpå som tar klicket.
    <embed
      src={`${src}${VISNINGSPARAMETRAR}`}
      type="application/pdf"
      className="w-full h-full pointer-events-none bg-white"
      aria-label={alt || 'PDF'}
    />
  );
};
